import type { ComponentProps } from 'react';
import type { Badge } from '@/components/Badge';
import type { IconName } from '@/components/Icon/iconMap';
import { isContractStatus } from '@/constants/contract';
import type { ApiResponseBody } from '@/lib/api/api-type-helper';

// 契約ステータスの型定義
export type ContractStatus = ApiResponseBody<'/v2/contract-ledger/contracts/{contractId}/profile-header', 'get'>['contract']['contractStatus'] | ApiResponseBody<'/v2/contract-ledger/employment-contracts', 'get'>['items'][0]['contractStatus'];

// Badge variant の型定義
export type BadgeVariant = ComponentProps<typeof Badge>['variant'];

// ステータスに基づいてBadgeのvariantを返すユーティリティ関数
export const getContractStatusBadgeVariant = (status: ContractStatus): BadgeVariant => {
  switch (status) {
    case 'EXECUTED':
      return 'mutedSuccess';
    case 'DRAFT':
    case 'WAITING':
      return 'neutralMuted';
    // 契約終了は期間満了で必ず到達する通常の終着点なので、注意色ではなくニュートラルで表す
    case 'TERMINATED':
      return 'neutralMuted';
    case 'CANCELLED':
      return 'mutedWarning';
    default:
      return 'neutralMuted';
  }
};

/**
 * ステータスの分類を返すユーティリティ関数
 *
 * `closed`（契約終了）は `cancelled`（破棄）と分けている。契約終了は期間満了で必ず到達する
 * 通常の終着点で、破棄は人が意図して落とした状態なので、色もアイコンも揃えない。
 */
const getContractStatusCategory = (status: ContractStatus): 'active' | 'pending' | 'closed' | 'cancelled' => {
  switch (status) {
    case 'EXECUTED':
      return 'active';
    case 'DRAFT':
    case 'WAITING':
      return 'pending';
    case 'TERMINATED':
      return 'closed';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return 'pending';
  }
};

/**
 * ステータスに対応するアイコン名を返すユーティリティ関数。
 *
 * 色は持たない。ステータスの色は Badge の variant が単一の定義を持ち、
 * アイコンは `inheritColor` でその文字色を継承する（定義を二重化しない）。
 */
export const getContractStatusIconName = (status: ContractStatus): IconName => {
  switch (getContractStatusCategory(status)) {
    case 'active':
      return 'CircleCheckIcon';
    case 'pending':
      return 'LoaderCircleIcon';
    case 'closed':
      return 'ArrowRightToLineIcon';
    case 'cancelled':
      return 'AlertTriangleIcon';
  }
};

/**
 * BE の `BadgeValue.code`（ContractStatus 相当）からアイコン名を解決する。
 * ContractStatus 以外のコードには `undefined` を返す。
 */
export function contractStatusBadgeIcon(code: string): IconName | undefined {
  return isContractStatus(code) ? getContractStatusIconName(code) : undefined;
}